import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {RawAlternative, RawAnchor} from '../api/types'
import {fetchAlternative, fetchAnchors} from '../api/client'
import {SITE_URL} from '../config'
import {ensureMotionPermission, isLikelyARCapable} from '../engine/engine'
import {track, trackOnce} from '../analytics'
import {assetUrl, isGeoLocated} from '../scene/mapping'
import type {QuestSession, QuestStatus} from '../scene/questSession'
import {startQuestSession} from '../scene/questSession'
import {QuestLanding} from './QuestLanding'

type Phase = 'loading' | 'landing' | 'starting' | 'ar' | 'preview' | 'error'

const ALIGN_HINTS: Record<string, string> = {
  'starting': 'Starting sensors…',
  'waiting-gps': 'Waiting for a steady GPS signal… works best outdoors',
  'waiting-heading': 'Finding north — hold your phone upright and stand still',
  'gps-denied': 'Location denied — enable it in Settings to place checkpoints',
}

export function QuestView({alternativeId}: {alternativeId: string}) {
  const [phase, setPhase] = useState<Phase>('loading')
  const [alt, setAlt] = useState<RawAlternative | null>(null)
  const [anchors, setAnchors] = useState<RawAnchor[]>([])
  const [errorMsg, setErrorMsg] = useState('')
  const [status, setStatus] = useState<QuestStatus | null>(null)
  const [toast, setToast] = useState('')
  const [showDebug, setShowDebug] = useState(
    new URLSearchParams(window.location.search).get('debug') === '1',
  )

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sessionRef = useRef<QuestSession | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const arStartingRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([fetchAlternative(alternativeId), fetchAnchors(alternativeId)])
      .then(([a, list]) => {
        if (cancelled) return
        setAlt(a)
        setAnchors(list)
        setPhase('landing')
        track('page_view', {
          alternativeId,
          meta: {checkpoints: list.length, arCapable: isLikelyARCapable()},
        })
      })
      .catch((err) => {
        if (cancelled) return
        setErrorMsg(err instanceof Error ? err.message : String(err))
        setPhase('error')
        track('error', {alternativeId, meta: {stage: 'load'}})
      })
    return () => {
      cancelled = true
    }
  }, [alternativeId])

  useEffect(
    () => () => {
      sessionRef.current?.stop()
      if (toastTimer.current) clearTimeout(toastTimer.current)
    },
    [],
  )

  const geoCount = useMemo(() => anchors.filter((a) => isGeoLocated(a.lat, a.lng)).length, [anchors])

  const startAR = useCallback(async () => {
    if (!alt || !canvasRef.current || arStartingRef.current) return
    arStartingRef.current = true
    setPhase('starting')
    track('ar_started', {alternativeId})
    const motionOk = await ensureMotionPermission()
    if (!motionOk) {
      track('fallback_preview', {alternativeId, meta: {reason: 'motion_denied'}})
      // Surfaced via QuestLanding's note — a toast would never render here
      // because the toast pill only exists in the 'ar' phase (review finding).
      arStartingRef.current = false
      setErrorMsg('Motion access was denied — AR needs motion sensors. You can preview the checkpoints below.')
      setPhase('preview')
      return
    }
    try {
      setPhase('ar')
      // Camera denial rejects this promise quickly (the engine fast-fails and
      // cleans itself + the GPS/heading sensors up) — the catch is the single
      // fallback path.
      sessionRef.current = await startQuestSession(canvasRef.current, alt, anchors, {
        onStatus: (s) => {
          setStatus(s)
          // Alignment is the make-or-break funnel step — how many people who
          // start AR actually get content placed.
          if (s.alignment === 'aligned') trackOnce('aligned', {alternativeId})
          if (s.completed) trackOnce('completed', {alternativeId, meta: {total: s.total}})
        },
        // WebXR sessions can be ended by the system back gesture; without this
        // the HUD would keep rendering over a stopped scene.
        onSessionEnd: () => {
          sessionRef.current?.stop()
          sessionRef.current = null
          setPhase('landing')
        },
        onDiscovered: (a) => {
          setToast(`Found: ${a.name} ✓`)
          track('checkpoint_found', {
            alternativeId,
            anchorId: String(a.id),
            meta: {order: a.order},
          })
          if (toastTimer.current) clearTimeout(toastTimer.current)
          toastTimer.current = setTimeout(() => setToast(''), 3500)
        },
        onError: (err) => console.warn('[quest]', err),
      })
    } catch (err) {
      console.warn('[quest] failed to start', err)
      sessionRef.current?.stop()
      sessionRef.current = null
      setErrorMsg(err instanceof Error ? err.message : String(err))
      setPhase('preview')
      track('fallback_preview', {alternativeId, meta: {reason: 'ar_start_failed'}})
    } finally {
      arStartingRef.current = false
    }
  }, [alt, anchors])

  if (phase === 'loading') return <div className="center-note">Loading…</div>
  if (phase === 'error')
    return (
      <div className="center-note">
        <p>Couldn't load this alternative.</p>
        <p className="dim">{errorMsg}</p>
      </div>
    )
  if (phase === 'preview') return <QuestLanding alternativeId={alternativeId} note={errorMsg} />

  const cover = assetUrl(alt?.main_image_asset?.path)
  const name = alt?.public_name || alt?.name || 'AR experience'
  const nearest = status?.anchors
    .filter((a) => !a.discovered && a.distanceM !== null)
    .sort((a, b) => (a.distanceM ?? 0) - (b.distanceM ?? 0))[0]
  const aligned = status?.alignment === 'aligned'

  return (
    <div className="share-view">
      <canvas ref={canvasRef} className="ar-canvas" />

      {phase === 'landing' && (
        <div className="landing">
          {cover && <img className="cover" src={cover} alt="" />}
          <div className="landing-body">
            <p className="eyebrow">On-location AR</p>
            <h1>{name}</h1>
            {alt?.tagline && <p className="dim">{alt.tagline}</p>}
            <div className="notice">
              <strong>Heads up — web AR is approximate.</strong> Checkpoint markers can be
              off by 5–20&nbsp;m (15–60&nbsp;ft), especially near buildings. Follow the arrow
              and distance readout — when you're close, look around. For precise placement
              and the full experience, get the app.
            </div>
            <p className="dim">
              {geoCount} of {anchors.length} checkpoints are placed at this location. You'll
              be asked for <strong>motion</strong>, <strong>camera</strong> and{' '}
              <strong>location</strong> access — in that order.
            </p>
            {geoCount > 0 && isLikelyARCapable() ? (
              <button className="btn primary" onClick={startAR}>
                Start AR
              </button>
            ) : (
              <div className="notice">
                {geoCount === 0
                  ? 'This alternative has no geo-located checkpoints yet — preview them below.'
                  : 'This device can’t run camera AR — open on a phone for the full experience.'}
              </div>
            )}
            <button className="btn ghost" onClick={() => setPhase('preview')}>
              Preview checkpoints without camera
            </button>
          </div>
          <footer className="attribution">AR engine © 2026 Niantic Spatial, Inc.</footer>
        </div>
      )}

      {phase === 'starting' && <div className="center-note">Starting camera…</div>}

      {phase === 'ar' && (
        <div className="hud">
          <div className="hud-top">
            <span className="pill">
              {status ? `${status.foundCount} of ${status.total} found` : name}
            </span>
            <span className="hud-actions">
              <button
                className="pill btn-pill"
                onClick={() => setShowDebug((v) => !v)}
                aria-label="debug"
              >
                {showDebug ? '×' : '≈'}
              </button>
              <button className="pill btn-pill" onClick={() => sessionRef.current?.recenter()}>
                Re-center
              </button>
            </span>
          </div>

          <div className="hud-center">
            {status && status.loading.length > 0 && (
              <span className="pill">Loading {status.loading.join(', ')}…</span>
            )}
            {!aligned && status && (
              <span className="pill">{ALIGN_HINTS[status.alignment] ?? 'Calibrating…'}</span>
            )}
            {toast && <span className="pill toast">{toast}</span>}
            {aligned && status?.driftSuspected && (
              <span className="pill warn">Positions look off? Tap Re-center</span>
            )}
          </div>

          <div className="hud-bottom">
            {status?.completed ? (
              <div className="cta-card">
                <p>
                  All {status.total} checkpoints found — nicely done!
                </p>
                <a
                  className="btn primary"
                  href={SITE_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => track('cta_clicked', {alternativeId, meta: {at: 'completed'}})}
                >
                  Get the full experience
                </a>
                <button
                  className="btn ghost"
                  onClick={() => sessionRef.current?.resetProgress()}
                >
                  Start over
                </button>
              </div>
            ) : aligned && nearest ? (
              <div className="nav-card">
                <span className="chevron" style={{transform: `rotate(${nearest.relativeBearingCssDeg ?? 0}deg)`}}>
                  ➤
                </span>
                <span>
                  <strong>{nearest.name}</strong>
                  <br />
                  <span className="dim">{formatDistance(nearest.distanceM)}</span>
                </span>
              </div>
            ) : null}
          </div>

          {showDebug && status && <DebugOverlay status={status} />}
        </div>
      )}
    </div>
  )
}

function formatDistance(m: number | null): string {
  if (m === null) return '—'
  if (m < 1000) return `${Math.round(m)} m`
  return `${(m / 1000).toFixed(1)} km`
}

function DebugOverlay({status}: {status: QuestStatus}) {
  const d = status.debug
  return (
    <div className="debug-overlay">
      <div>engine: {status.engineKind ?? '—'}</div>
      <div>state: {d.state}</div>
      <div>
        gps: {d.gps ? `${d.gps.lat.toFixed(6)}, ${d.gps.lng.toFixed(6)} ±${Math.round(d.gps.accuracy)}m (${Math.round(d.gps.ageMs / 1000)}s, streak ${d.gpsStreak})` : '—'}
        {d.gpsError ? ` ⚠ ${d.gpsError}` : ''}
      </div>
      <div>
        heading: {d.headingDeg !== null ? `${d.headingDeg.toFixed(1)}° (${d.headingSource}, spread ${d.headingSpreadDeg?.toFixed(1) ?? '—'}°)` : '—'}
      </div>
      <div>camYaw α: {d.cameraYawDeg !== null ? `${d.cameraYawDeg.toFixed(1)}°` : '—'}</div>
      <div>θ applied: {d.appliedThetaDeg !== null ? `${d.appliedThetaDeg.toFixed(1)}°` : '—'}</div>
      <div>drift: {d.driftM !== null ? `${d.driftM.toFixed(1)} m` : '—'}</div>
      {status.anchors.map((a) => (
        <div key={a.id}>
          #{a.order} {a.name}: {formatDistance(a.distanceM)} [{a.contentState}]{' '}
          {a.discovered ? '✓' : ''}
        </div>
      ))}
    </div>
  )
}
